import {
  AccessControlProps,
  AccessControlStatusEnum,
  DeliveryManStatusEnum,
  translatedDeliveryManStatus,
} from 'modules/access-controls/types';
import deliveryMenKeys from 'modules/delivery-men/util/delivery-men-keys';
import React from 'react';
import {IoCheckmark, IoClose, IoTrashBin} from 'react-icons/io5';
import StatusButton from 'shared/components/status-button';
import List from 'shared/layout/list';
import ApiDynamicButton from 'shared/migration-components/api-dynamic-button';
import {Operators} from 'shared/migration-components/filter-list/types';
import useProjection from 'shared/migration-hooks/use-projection';

const DeliveryMen: React.FC = () => {
  const projection = useProjection<AccessControlProps>('deliveryMen', [
    'userName',
    'cityName',
    'maxDeliveryQuantity',
    'status',
    'deliveryManStatus',
    'numberOfDeliveries',
    'averageDeliveryDuration',
    'id',
  ]);

  return (
    <List<AccessControlProps>
      {...{
        titleBar: {
          title: 'Entregadores',
          actionButton: {title: 'NOVO ENTREGADOR', to: 'create'},
        },
        table: {
          url: '/access-controls',
          projection: {
            ...projection,
            options: [
              'userName',
              'cityName',
              'maxDeliveryQuantity',
              {
                value: 'averageDeliveryDuration',
                column: (row) =>
                  `${
                    row.averageDeliveryDuration
                      ? (row.averageDeliveryDuration / 60).toFixed(2)
                      : 0
                  } minutos`,
              },
              'numberOfDeliveries',
              {
                value: 'status',
                column: (row) => (
                  <StatusButton
                    text={
                      row.status === AccessControlStatusEnum.approved
                        ? 'Aprovado'
                        : row.status === AccessControlStatusEnum.reproved
                        ? 'Reprovado'
                        : 'Aguardando análise'
                    }
                    variant={
                      row.status === AccessControlStatusEnum.approved
                        ? 'success'
                        : row.status === AccessControlStatusEnum.reproved
                        ? 'error'
                        : 'warn'
                    }
                  />
                ),
              },
              {
                value: 'deliveryManStatus',
                column: (row) => (
                  <StatusButton
                    text={translatedDeliveryManStatus[row.deliveryManStatus]}
                    variant={
                      row.deliveryManStatus === DeliveryManStatusEnum.ONLINE
                        ? 'success'
                        : row.deliveryManStatus ===
                          DeliveryManStatusEnum.OFFLINE
                        ? 'warn'
                        : 'error'
                    }
                  />
                ),
              },
              'user.pixKey',
              {
                value: 'id',
                label: 'Ações',
                column: (row) => (
                  <div style={{display: 'flex', flexDirection: 'row', gap: 10}}>
                    {row.status !==
                      AccessControlStatusEnum.awaiting_approval && (
                      <ApiDynamicButton
                        {...{
                          request: {
                            url: `/access-controls/${row.id}`,
                            errorTitle: 'Erro ao remover solicitação',
                            method: 'DELETE',
                            successTitle: 'Remover',
                            successMessage: 'Solicitação removida com sucesso!',
                            field: row.id,
                          },
                          keys: {deleteInfiniteList: deliveryMenKeys.list()},
                          button: {
                            icon: <IoTrashBin />,
                            tooltip: 'Remover',
                          },
                        }}
                      />
                    )}
                    {(!row.status ||
                      row.status ===
                        AccessControlStatusEnum.awaiting_approval) && (
                      <ApiDynamicButton
                        {...{
                          request: {
                            url: `/access-controls/${row.id}/approve`,
                            errorTitle: 'Erro ao aprovar entregador',
                            method: 'PATCH',
                            successTitle: 'Aprovar entregador',
                            successMessage: 'Entregador aprovado com sucesso!',
                            field: row.id,
                          },
                          keys: {updateInfiniteList: deliveryMenKeys.list()},
                          button: {
                            icon: <IoCheckmark />,
                            tooltip: 'Aprovar entregador',
                          },
                        }}
                      />
                    )}
                    {(!row.status ||
                      row.status ===
                        AccessControlStatusEnum.awaiting_approval) && (
                      <ApiDynamicButton
                        {...{
                          request: {
                            url: `/access-controls/${row.id}/reprove`,
                            errorTitle: 'Erro ao rejeitar entregador',
                            method: 'PATCH',
                            successTitle: 'Rejeitar entregador',
                            successMessage: 'Entregador rejeitado com sucesso!',
                            field: row.id,
                          },
                          keys: {updateInfiniteList: deliveryMenKeys.list()},
                          button: {
                            icon: <IoClose />,
                            tooltip: 'Rejeitar entregador',
                          },
                        }}
                      />
                    )}
                  </div>
                ),
              },
            ],
          },
          translatedPropsKey: 'AccessControlProps',
        },
        extraParams: {
          permissionGroupId: {
            operator: Operators.Equals,
            value: '6761a490bc11c17e26ccd561',
          },
        },
        fixedColumns: ['status'],
        queryKey: deliveryMenKeys.list(),
      }}
    />
  );
};

export default DeliveryMen;
